import type { AnalyticsEventPayload, AnalyticsProvider } from '@/services/analytics';
import { AnalyticsEvents, type AnalyticsEventName } from '@/services/analytics/events';
import type { ICookieConsent } from '@/utils/cookieConsent.utils';
import { loadScript } from '@/utils/script.utils';

interface IMetaPixelFunction {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[][];
  push?: IMetaPixelFunction;
  loaded?: boolean;
  version?: string;
}

declare global {
  interface Window {
    fbq?: IMetaPixelFunction;
    _fbq?: IMetaPixelFunction;
  }
}

const META_PIXEL_ID = '2010844002809485';
const META_PIXEL_SCRIPT_SRC = 'https://connect.facebook.net/en_US/fbevents.js';

/**
 * Maps internal analytics event names to Meta Pixel standard events.
 * Only events that have a meaningful Meta standard event mapping are included.
 */
const EVENT_MAP: Partial<Record<AnalyticsEventName, string>> = {
  [AnalyticsEvents.auth.sign_up]: 'CompleteRegistration',
  [AnalyticsEvents.checkout.purchase]: 'Purchase',
  [AnalyticsEvents.billing.trialStarted]: 'StartTrial',
  [AnalyticsEvents.checkout.submit]: 'InitiateCheckout',
};

export class MetaPixelAnalytics implements AnalyticsProvider {
  public readonly name = 'meta-pixel';
  public readonly consentCategories = ['marketing'] as const;

  public init(): Promise<void> {
    if (!window.fbq) {
      const fbq: IMetaPixelFunction = (...args) => {
        if (fbq.callMethod) fbq.callMethod(...args);
        else fbq.queue!.push(args);
      };
      fbq.push = fbq;
      fbq.loaded = true;
      fbq.version = '2.0';
      fbq.queue = [];
      window.fbq = fbq;
      window._fbq = window._fbq ?? fbq;
    }
    window.fbq('consent', 'grant');
    window.fbq('init', META_PIXEL_ID);
    return loadScript(META_PIXEL_SCRIPT_SRC);
  }

  public setConsent(consent: ICookieConsent | null): void {
    window.fbq?.('consent', consent?.marketing ? 'grant' : 'revoke');
  }

  public trackPageview(): void {
    window.fbq?.('track', 'PageView');
  }

  public trackEvent(name: AnalyticsEventName, payload?: AnalyticsEventPayload): void {
    const metaEvent = EVENT_MAP[name];
    if (!metaEvent) return;
    if (payload && Object.keys(payload).length > 0) window.fbq?.('track', metaEvent, payload);
    else window.fbq?.('track', metaEvent);
  }
}
